import React, { useState } from "react";
import { Plus } from "lucide-react";
import Page from "@drillflo/drillcore/lib/Page";
import { useEffect } from "react";
import {
  useDrillViewData,
  usePlaybackData,
  useFieldEditorData,
  usePageManagementData,
} from "../hooks/useFieldEditorShared.tsx";
import AudioWaveform from "../audioWaveform.tsx";

export default function FieldEditorTimeline() {
  // Get data from context hooks
  const { drill } = useDrillViewData();
  const {
    playbackState,
    setPlaybackState,
    handlePageChange,
    handleCountChange,
    handlePlayDrill,
    handleStopDrill,
  } = usePlaybackData();
  const { fieldEditorData } = useFieldEditorData();
  const { handleAddPageAtIndex, handleDeletePageAtIndex } =
    usePageManagementData();

  // Extract values from playback state and field editor data
  const currentPage = playbackState.currentPage;
  const currentCount = playbackState.currentCount;
  const editMode = fieldEditorData.editMode;
  const setEditMode = fieldEditorData.setEditMode;
  const [showDeletePageModal, setShowDeletePageModal] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(0);
  const cancelDeletePage = () => {
    setShowDeletePageModal(false);
  };
  const confirmDeletePage = () => {
    handleDeletePageAtIndex(pageToDelete);
    setShowDeletePageModal(false);
  };
  // Default values for missing props
  const commentCounts: number[] = [];
  const handleAddPage = () => handleAddPageAtIndex(drill?.pages.length || 0);
  const handleDeletePage = (
    index: number,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    handleDeletePageAtIndex(index);
  };
  const [expanded, setExpanded] = useState(true);
  const [pages, setPages] = useState<Page[]>(drill?.pages ?? []);

  // Calculate the total width and current position for playhead
  const calculatePlayheadPosition = () => {
    if (!pages) return 0;

    let totalWidthPx = 0;
    let currentPositionPx = 0;

    for (let index = 0; index < pages.length; index++) {
      const isLastPage = index === pages.length - 1;

      // Match the width logic from your page area
      const countsForWidth = isLastPage ? 4 : pages[index + 1]?.counts ?? 0;

      const pageWidthPx = Math.max(10, countsForWidth * 20);

      if (index < currentPage) {
        currentPositionPx += pageWidthPx;
      } else if (index === currentPage) {
        const countsForProgress = isLastPage
          ? 4
          : pages[index + 1]?.counts ?? 0;

        const progress =
          countsForProgress > 0
            ? Math.min(Math.max(currentCount / countsForProgress, 0), 1)
            : 0;

        currentPositionPx += pageWidthPx * progress;
      }

      totalWidthPx += pageWidthPx;
    }

    return totalWidthPx > 0 ? (currentPositionPx / totalWidthPx) * 100 : 0;
  };

  useEffect(() => {
    calculatePlayheadPosition();
  }, [currentPage, currentCount]);

  useEffect(() => {
    const onDrillUpdated = () => {
      setPages(drill?.pages ?? []);
    };

    drill?.engine?.on("drill-modified", onDrillUpdated);
    drill?.engine?.on("drill-updated-from-collaboration", onDrillUpdated);
    onDrillUpdated();

    return () => {
      drill?.engine?.removeOn("drill-modified", onDrillUpdated);
      drill?.engine?.removeOn(
        "drill-updated-from-collaboration",
        onDrillUpdated
      );
    };
  }, [drill]);

  // Handle count tick click
  const handleCountClick = (pageIndex: number, countIndex: number) => {
    // If we need to change pages, do that first
    if (!drill) return;
    if (pageIndex !== drill.selectedPageIndex) {
      handlePageChange(pageIndex);
      // The handlePageChange will reset currentCount to 0, so we need to set it after
      // We'll use a small delay to ensure the page change is processed
      // requestAnimationFrame(() => {
      if (handleCountChange) {
        handleCountChange(countIndex + 1);
      }
      // });
    } else {
      // If we're already on the correct page, just change the count

      if (handleCountChange) {
        handleCountChange(countIndex + 1);
      }
    }
  };
  const [isDragging, setIsDragging] = React.useState(false);
  const lastTickRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const stopDrag = () => setIsDragging(false);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      window.removeEventListener("mouseup", stopDrag);
    };
  }, []);

  // useEffect(() => {
  //   console.log(currentCount);
  // }, [currentCount]);

  return (
    <div className="bg-white border-t shadow-md bottom-0 fixed w-full h-20">
      <div className="max-w-screen-xl mx-auto px-4 py-3">
        <div className="flex items-center space-x-4">
          {/* Play/Pause button */}
          <button
            type="button"
            data-tour="playback"
            aria-label={playbackState.isPlaying ? "Pause drill" : "Play drill"}
            onClick={
              playbackState.isPlaying ? handleStopDrill : handlePlayDrill
            }
            className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
          >
            {playbackState.isPlaying ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="6" y="4" width="4" height="16"></rect>
                <rect x="14" y="4" width="4" height="16"></rect>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            )}
          </button>
          <button
            type="button"
            aria-pressed={expanded}
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "Collapse view" : "Expand view"}
            className={`inline-flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 transition-transform`}
            style={{ height: 20, width: "auto", padding: 6 }}
          >
            {/* Caret icon container */}
            <span
              aria-hidden
              className="flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 transition-colors"
              style={{
                width: 40 - 12,
                height: 40 - 12,
                transform: `rotate(${expanded ? 180 : 0}deg)`,
                transition:
                  "transform 220ms cubic-bezier(.2,.9,.2,1), box-shadow 160ms",
                boxShadow: expanded
                  ? "0 6px 18px rgba(59,130,246,0.12)"
                  : undefined,
              }}
            >
              {/* Chevron (caret) SVG */}
              <svg
                width={Math.max(12, (40 - 12) * 0.6)}
                height={Math.max(12, (40 - 12) * 0.6)}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-slate-700"
                style={{
                  transition: "transform 220ms cubic-bezier(.2,.9,.2,1)",
                  transform: expanded
                    ? "translateY(0px) scale(1.02)"
                    : "translateY(0px) scale(1)",
                }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </button>
          {/* Timeline */}
          <div className="relative flex-1 overflow-x-scroll bg-white rounded-md shadow-inner">
            {/* Ruler */}
            {/* {expanded && (
              <div className="flex h-6 border-b border-gray-200 relative select-none">
                <div className="w-full h-1 bg-blue-500 z=[-10]">
                  <AudioWaveform
                    audioUrl={drill?.getCustomData("audioFile") as string}
                    onTrimComplete={() => { }}
                    editable={false}

                  />
                </div>
              </div>
            )} */}
            {/* Page area */}
            <div className="flex h-10 relative  ">
              {drill &&
                pages.map((page, index) => {
                  const isSelected = playbackState.isPlaying
                    ? playbackState.currentPage === index
                    : playbackState.currentPage === index;

                  const pageMultiplier = expanded ? 20 : 15;
                  const pageCounts =
                    index === 0
                      ? pages[index + 1]?.counts
                      : pages[index + 1]?.counts || index !== pages.length - 1
                        ? pages[index + 1]?.counts
                        : 1;

                  const pageWidth = Math.max(
                    10,
                    index === pages.length - 1
                      ? 8 * pageMultiplier
                      : pageCounts * pageMultiplier
                  );

                  return (
                    <div
                      key={`page-${index}`}
                      className={`group relative border border-gray-200 rounded-md cursor-pointer transition-all  ${isSelected
                        ? "bg-blue-50 border-blue-400"
                        : "bg-white hover:bg-gray-50"
                        }`}
                      style={{
                        width: `${pageWidth}px`,
                        minWidth: `${pageWidth}px`,
                      }}
                      onClick={() => handlePageChange(index)}
                    >
                      {/* Page label */}
                      <div className="absolute top-1 left-6 text-xs text-gray-700">
                        {index === 0 ? "Page 0" : `Page ${index}`}
                      </div>
                      <div className="absolute top-4 left-6 text-xs text-gray-500">
                        {/* {pageCounts} count{pageCounts > 1 ? "s" : ""} */}
                      </div>
                      {drill && pages.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDeletePageModal(true);
                            setPageToDelete(index);
                          }}
                          className="absolute top-1 right-4 p-1 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 text-gray-400 hover:text-red-500"
                          title="Delete page"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      )}
                      {/* Count ticks spanning height */}
                      {[...Array(pageCounts)].map((_, countIndex) => (
                        <div
                          key={`page-tick-${index}-${countIndex}`}
                          className="hidden absolute top-0 bottom-0 border-l border-gray-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                          style={{
                            left: `${(countIndex / pageCounts) * 100}%`,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCountClick(index, countIndex);
                          }}
                        />
                      ))}

                      {/* Progress within this page */}
                      {isSelected && (
                        <div
                          className="absolute bottom-0 left-0 h-1 bg-blue-500"
                          style={{
                            width: `${Math.min(
                              (currentCount /
                                (index === 0 ? pageCounts : pageCounts)) *
                              100,
                              100
                            )}%`,
                          }}
                        ></div>
                      )}
                      <button
                        type="button"
                        data-tour={index === 0 ? "add-page" : undefined}
                        className="absolute right-[-13px] top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-dashed border-slate-200 bg-slate-200/90 p-1 text-slate-800 hover:border-slate-300 hover:text-slate-900 md:flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddPageAtIndex(index + 1);
                        }}
                        aria-label="Add page after"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}

              {/* Playhead */}
              {/* <div
                className="absolute top-0 bottom-0 w-0.5 bg-blue-600 z-10"
                style={{
                  left: `${calculatePlayheadPosition()}%`,
                  transition: isPlaying ? "left 0.05s linear" : "none",
                }}
              ></div> */}
            </div>

            {expanded && (
              <div
                className="flex h-6 border-b border-gray-200 relative select-none"
                onMouseLeave={() => setIsDragging(false)}
              >
                {drill &&
                  pages.map((page, index) => {
                    const pageCounts =
                      index === 0
                        ? pages[index + 1]?.counts
                        : pages[index + 1]?.counts || index !== pages.length - 1
                          ? pages[index + 1]?.counts
                          : 1;
                    const pageWidth = Math.max(
                      10,
                      index === 0 ? pageCounts * 20 : pageCounts * 20
                    );

                    return (
                      <div
                        key={`ruler-${index}`}
                        style={{
                          width: `${pageWidth}px`,
                          minWidth: `${pageWidth}px`,
                        }}
                        className="relative flex items-end"
                      >
                        {[...Array(pageCounts)].map((_, countIndex) => (
                          <div
                            key={`tick-${index}-${countIndex}`}
                            className={`absolute top-0  h-full w-5  border-l cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors ${countIndex === 0
                              ? "border-gray-700 "
                              : "border-gray-300 "
                              } `}
                            style={{
                              left: `${(countIndex / pageCounts) * 100}%`,
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setIsDragging(true);
                              lastTickRef.current = `${index}-${countIndex}`;
                              handleCountClick(index, countIndex);
                            }}
                            onMouseEnter={() => {
                              if (isDragging) {
                                const id = `${index}-${countIndex}`;
                                if (id !== lastTickRef.current) {
                                  lastTickRef.current = id;
                                  handleCountClick(index, countIndex);
                                }
                              }
                            }}
                          >
                            <span className="absolute top-2 left-1 text-[10px] text-gray-500 hover:text-blue-600">
                              {countIndex + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showDeletePageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-red-600"
              >
                <path d="M3 6h18"></path>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-center mb-2">
              Delete Page
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">Page {pageToDelete}</span>? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDeletePage}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePage}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Page
              </button>
            </div>
          </div>
        </div>

      )}
    </div>
  );
}
